import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, FileEdit, LayoutTemplate, ArrowRight, Plus, 
  BookOpen, Crown, Shield, CheckCircle, Sparkles, AlertTriangle, X, Download,
  Users, Mail, Target, Heart, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PublicHomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);

  // Detect in-app browsers (Google app, Facebook, Instagram, etc.)
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    
    // Check for known in-app browser signatures
    if (/FBAN|FBAV|Instagram|GSA\/|Line\/|Twitter|Snapchat|TikTok/i.test(ua)) {
      return true;
    }
    
    // iOS WebView detection: iOS device with WebKit but NOT Safari or Chrome
    if (isIOS && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua)) {
      return true;
    }
    
    // Additional check: iOS with "Mobile" but missing browser identifiers
    if (isIOS && /Mobile/i.test(ua) && !/CriOS|FxiOS|OPiOS|Safari/i.test(ua)) {
      return true;
    }
    
    return false;
  };

  // Check for in-app browser on mount
  useEffect(() => {
    if (isInAppBrowser()) {
      setShowBrowserWarning(true);
    }
  }, []);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${API}/resumes/templates`);
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Get unique categories
  const categories = ['All', ...new Set(templates.map(t => t.category))];

  // Filter templates by category
  const filteredTemplates = selectedCategory === 'All' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const handleCreateResume = () => {
    navigate('/create-resume');
  };

  const handleRefineResume = () => {
    // Navigate directly to refine - no login required until download
    navigate('/refine');
  };

  const handleConvertResume = () => {
    navigate('/convert');
  };

  const handleTemplateSelect = (templateId) => {
    // Store selected template and go to create/convert flow
    sessionStorage.setItem('selectedTemplateId', templateId);
    navigate('/create-resume');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center group-hover:bg-primary/90 transition-colors">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-primary hidden sm:block">
                FixResume24
              </span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/" 
                className="text-primary font-medium"
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link 
                to="/match-score" 
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid="nav-match-score"
              >
                ATS Score Checker
              </Link>
              {user && (
                <Link 
                  to="/my-resumes" 
                  className="text-muted-foreground hover:text-primary transition-colors font-medium"
                  data-testid="nav-resumes"
                >
                  My Resumes
                </Link>
              )}
              <Link 
                to="/resume-template/software-engineer" 
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid="nav-examples"
              >
                Resume Examples
              </Link>
              <Link 
                to="/about" 
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                data-testid="nav-about"
              >
                About Us
              </Link>
            </nav>

            {/* Auth Button */}
            {user ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground hidden sm:block">
                  Hi, {user.name?.split(' ')[0]}
                </span>
                <Link to="/profile">
                  <Button variant="outline" size="sm" data-testid="profile-btn">
                    Profile
                  </Button>
                </Link>
              </div>
            ) : (
              <Link to="/login">
                <Button data-testid="sign-in-btn">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* In-App Browser Warning Banner */}
      {showBrowserWarning && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Browser Compatibility Notice
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  You&apos;re using an in-app browser where file downloads may not work properly. 
                  For the best experience, please open <strong>fixresume24.com</strong> in <strong>Safari</strong> or <strong>Chrome</strong>.
                </p>
              </div>
              <button 
                onClick={() => setShowBrowserWarning(false)}
                className="text-amber-600 hover:text-amber-800 p-1"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* NEW: Job Match Score Feature Card - Primary CTA */}
        <div className="mb-12">
          <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 via-white to-accent/5 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex flex-col lg:flex-row items-center">
                {/* Left: Content */}
                <div className="flex-1 p-8 lg:p-10">
                  <Badge className="bg-green-100 text-green-700 mb-4">
                    <Target className="h-3 w-3 mr-1" />
                    Free ATS Score Checker
                  </Badge>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary mb-3">
                    How Well Does Your Resume Match?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Upload your resume and paste any job description. Get instant feedback on your match score, missing keywords, and AI-powered suggestions to improve.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/match-score">
                      <Button size="lg" className="gap-2" data-testid="hero-match-score-btn">
                        <Target className="h-5 w-5" />
                        Check Your Score - Free
                      </Button>
                    </Link>
                  </div>
                  <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      No login required
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="h-4 w-4 text-blue-600" />
                      100% Free
                    </span>
                  </div>
                </div>
                {/* Right: Visual */}
                <div className="flex-shrink-0 p-8 hidden lg:block">
                  <div className="relative">
                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center shadow-lg">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-green-600">85%</p>
                        <p className="text-xs text-green-700 font-medium">Match Score</p>
                      </div>
                    </div>
                    <div className="absolute -top-2 -right-2 bg-white rounded-full p-2 shadow-md">
                      <Sparkles className="h-5 w-5 text-amber-500" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-primary tracking-tight mb-4">
            Build Your Perfect Resume
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Create, refine, or convert your resume with AI-powered tools and 29+ professional templates
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Button 
              size="lg" 
              onClick={handleCreateResume}
              className="gap-2"
              data-testid="create-resume-btn"
            >
              <Plus className="h-5 w-5" />
              Create Resume
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={handleRefineResume}
              className="gap-2"
              data-testid="refine-resume-btn"
            >
              <FileEdit className="h-5 w-5" />
              Refine Resume
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={handleConvertResume}
              className="gap-2"
              data-testid="convert-resume-btn"
            >
              <LayoutTemplate className="h-5 w-5" />
              Convert to Template
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-3">
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              <Shield className="h-4 w-4 mr-1" />
              ATS-Optimized
            </Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              <Sparkles className="h-4 w-4 mr-1" />
              AI-Powered
            </Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              <CheckCircle className="h-4 w-4 mr-1" />
              No Login Required for Free Templates
            </Badge>
          </div>
          
          {/* Trust indicator - Download count */}
          <div className="mt-6 flex items-center justify-center gap-2 text-muted-foreground">
            <Download className="h-5 w-5 text-green-600" />
            <span className="text-lg font-semibold text-slate-700">1500+</span>
            <span>professionals have built their perfect resume with us</span>
          </div>
        </div>

        {/* Templates Section */}
        <section className="mb-16" data-testid="templates-section">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Choose a Template</h2>
              <p className="text-muted-foreground">Select a template to start building your resume</p>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-${category.toLowerCase()}`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Templates Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-0">
                    <div className="h-64 bg-slate-200 rounded-t-lg"></div>
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTemplates.map((template) => (
                <TemplateCard 
                  key={template.id} 
                  template={template} 
                  onSelect={handleTemplateSelect}
                />
              ))}
            </div>
          )}
          
          {/* Match Score CTA below templates */}
          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-3">Already have a resume?</p>
            <Link to="/match-score">
              <Button variant="outline" className="gap-2">
                <Target className="h-4 w-4" />
                Check Your ATS Match Score - Free
              </Button>
            </Link>
          </div>
        </section>

        {/* Features Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Why Choose FixResume24?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Plus className="h-6 w-6" />}
              title="Create from Scratch"
              description="Build a professional resume with our easy-to-use form. No experience needed."
            />
            <FeatureCard
              icon={<FileEdit className="h-6 w-6" />}
              title="AI-Powered Refinement"
              description="Fix grammar, improve clarity, and enhance your existing resume with AI."
            />
            <FeatureCard
              icon={<LayoutTemplate className="h-6 w-6" />}
              title="29+ Templates"
              description="Choose from professional, modern, creative, and ATS-optimized templates."
            />
          </div>
        </section>

        {/* Pricing Preview */}
        <section className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Simple Pricing</h2>
            <p className="text-muted-foreground">Try everything for free. Pay only when you download.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="border-slate-200">
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-lg mb-2">Free Download</h3>
                <p className="text-3xl font-bold text-primary mb-2">₹0</p>
                <p className="text-sm text-muted-foreground mb-4">With watermark</p>
                <ul className="text-sm text-left space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    11 Free Templates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Basic AI Refinement
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    PDF Download
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-accent/50 bg-accent/5">
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-lg mb-2">Single Template</h3>
                <p className="text-3xl font-bold text-accent mb-2">₹29</p>
                <p className="text-sm text-muted-foreground mb-4">One-time download</p>
                <ul className="text-sm text-left space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Any Premium Template
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    No Watermark
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Single Download
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-primary/50 bg-primary/5 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-white">Best Value</Badge>
              </div>
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-lg mb-2">Premium Access</h3>
                <p className="text-3xl font-bold text-primary mb-2">₹79</p>
                <p className="text-sm text-muted-foreground mb-4">24-hour access</p>
                <ul className="text-sm text-left space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    All 29 Templates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Full ATS Optimization
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    No Watermark
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Unlimited Downloads
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* About Us Section */}
        <section id="about" className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <Badge variant="outline" className="mb-4">
                <Users className="h-3 w-3 mr-1" />
                About Us
              </Badge>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Empowering professionals to land their dream jobs with perfectly crafted resumes.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <Card className="border-slate-200 text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Our Vision</h3>
                  <p className="text-sm text-muted-foreground">
                    To become the most trusted resume transformation platform, helping millions of professionals present their best selves to employers.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Heart className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold mb-2">Why We Built This</h3>
                  <p className="text-sm text-muted-foreground">
                    We noticed talented professionals getting overlooked due to poorly formatted resumes. FixResume24 bridges that gap with smart, ATS-optimized templates.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <Zap className="h-6 w-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold mb-2">What Sets Us Apart</h3>
                  <p className="text-sm text-muted-foreground">
                    AI-powered ATS optimization, instant preview, 29+ professional templates, and a commitment to your privacy. Your resume, your success.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-xl p-8 border border-slate-200">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <FileText className="h-10 w-10 text-white" />
                  </div>
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">FixResume24</h3>
                  <p className="text-muted-foreground">
                    Built with passion for job seekers worldwide. We believe everyone deserves a resume that truly represents their skills and potential. 
                    Our platform combines cutting-edge AI technology with professional design principles to help you stand out in today&apos;s competitive job market.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Us Section */}
        <section id="contact" className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <Badge variant="outline" className="mb-4">
              <Mail className="h-3 w-3 mr-1" />
              Contact Us
            </Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Have questions, feedback, or need support? We&apos;d love to hear from you!
            </p>

            <Card className="border-slate-200">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                <p className="text-muted-foreground mb-4">
                  We&apos;d love to hear from you!
                </p>
                <a 
                  href="mailto:support@fixresume24.com" 
                  className="inline-flex items-center gap-2 text-lg font-medium text-primary hover:underline"
                >
                  <Mail className="h-5 w-5" />
                  support@fixresume24.com
                </a>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-4">
          <div className="flex flex-wrap items-center justify-center gap-6 mb-2">
            <Link to="/match-score" className="text-sm text-slate-600 hover:text-primary transition-colors font-medium">
              ATS Score Checker
            </Link>
            <Link to="/about" className="text-sm text-slate-600 hover:text-primary transition-colors">
              About Us
            </Link>
            <a href="#contact" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Contact Us
            </a>
            <Link to="/terms" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy-policy" className="text-sm text-slate-600 hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </div>
          <p className="text-xs text-muted-foreground text-center max-w-xl">
            This tool enhances formatting and language clarity. Users are responsible for the accuracy of resume content.
          </p>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} FixResume24. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Template Card Component
const TemplateCard = ({ template, onSelect }) => {
  const [previewHtml, setPreviewHtml] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await fetch(`${API}/resumes/templates/${template.id}/preview`);
        if (response.ok) {
          const html = await response.text();
          setPreviewHtml(html);
        }
      } catch (error) {
        console.log('Preview fetch error');
      }
    };
    fetchPreview();
  }, [template.id, API]);

  return (
    <Card 
      className="group cursor-pointer hover:shadow-lg transition-all duration-300 overflow-hidden border-slate-200 hover:border-primary/50"
      onClick={() => onSelect(template.id)}
      data-testid={`template-card-${template.id}`}
    >
      <CardContent className="p-0">
        {/* Preview */}
        <div className="h-56 overflow-hidden bg-white border-b relative">
          {previewHtml ? (
            <div 
              className="transform scale-[0.35] origin-top-left w-[286%] h-[286%] pointer-events-none"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="h-full flex items-center justify-center bg-slate-50">
              <FileText className="h-12 w-12 text-slate-300" />
            </div>
          )}
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button variant="secondary" size="sm">
              Use This Template
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Info */}
        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-semibold text-slate-900 line-clamp-1">{template.name}</h3>
          </div>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            {template.isPremium ? (
              <Badge className="bg-amber-100 text-amber-700 text-xs">
                <Crown className="h-3 w-3 mr-1" />
                Premium
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50">
                Free
              </Badge>
            )}
            {template.ats_label && (
              <Badge variant="outline" className="text-xs">
                <Shield className="h-3 w-3 mr-1" />
                {template.ats_label}
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              {template.category}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
  <Card className="border-slate-200">
    <CardContent className="p-6 text-center">
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4 text-primary">
        {icon}
      </div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </CardContent>
  </Card>
);

export default PublicHomePage;
